import { ButtonWidget, PopoverAlertType, PopoverInstance, PopoverWidget } from '@tableau/widgets';
import { ReactChild } from 'enzyme/node_modules/@types/react';
import * as React from 'react';
import { ToolbarColorTheme, useToolbarColorThemeContext } from '../PublicTypes';
import { PopoverButtonContainerStyleName } from '../styles/ButtonStyles';
import { Button, ButtonProps } from './Button';

export interface PopoverButtonProps
  extends Pick<
    ButtonProps,
    'disabled' | 'isHidden' | 'onClick' | 'tabIndex' | 'description' | 'iconId' | 'id' | 'label' | 'showLabel' | 'testId'
  > {
  /** Action to be called on button click. Typically this just logs telemetry. */
  onToolbarItemClick?: () => void;
  /** Optionally adds an alert style to the popover. */
  popoverAlertType: PopoverAlertType | undefined;
  /** The content rendered inside of the popover. */
  popoverContent: ReactChild;
  /**
   * Will create `ButtonWidget`s in the footer which are aligned and spaced in a standardized way.
   * Note: In the toolbar, footer buttons will also close the popover on click.
   */
  footerButtons?: Array<ButtonWidget['props']>;
  /** A title is required for accessiblity, even if not shown visually. */
  popoverTitle: ReactChild;
  /**
   * Whether or not to display a title bar in the popover.
   * Note: that supplying a value to alertType will always show a title bar, regardless of this value.
   */
  showPopoverTitleBar?: boolean;
}

export function PopoverButton(props: PopoverButtonProps): JSX.Element {
  const colorTheme: ToolbarColorTheme = useToolbarColorThemeContext();
  const popoverInstance = React.useRef<PopoverInstance>(null);

  const { onToolbarItemClick, popoverAlertType, popoverContent, popoverTitle, footerButtons, showPopoverTitleBar, ...buttonProps } = props;

  const footerButtonsMemo = React.useMemo(() => {
    if (!footerButtons) return undefined;

    const processedButtons: Array<ButtonWidget['props']> = [];
    footerButtons.map((buttonProps) => {
      const newOnClick: React.MouseEventHandler<HTMLButtonElement & HTMLDivElement> = (e) => {
        // For button props provided outside the module, we need to handle the case where handleClick (deprecated)
        // is set instead of onClick
        if (buttonProps.handleClick) {
          buttonProps.handleClick(e);
        } else if (buttonProps.onClick) {
          buttonProps.onClick(e);
        }
        popoverInstance.current?.toggle(false);
      };

      processedButtons.push({ ...buttonProps, handleClick: undefined, onClick: newOnClick });
    });
    return processedButtons;
  }, [footerButtons]);

  const handleTriggerButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    // onClick prop is provided by the PopoverWidget
    buttonProps.onClick?.(e);
    onToolbarItemClick?.();
  };

  const button = <Button {...buttonProps} key={buttonProps.testId} buttonType="popoverButton" onClick={handleTriggerButtonClick} />;

  return (
    <div className={PopoverButtonContainerStyleName(colorTheme)}>
      <PopoverWidget
        alertType={popoverAlertType}
        footerButtons={footerButtonsMemo}
        placement="bottom-start"
        popoverContent={popoverContent}
        ref={popoverInstance}
        showTitleBar={showPopoverTitleBar}
        testId={buttonProps.testId}
        title={popoverTitle}
        triggerElement={button}
        width={400}
      />
    </div>
  );
}
